"use client";

import * as React from "react";
import { motion } from "motion/react";
import { Shield, Lock, Cpu, Database, CheckCircle2, Server } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const COMPLIANCE = [
  { name: "Identity & Access", icon: Lock, color: "from-emerald/15", accent: "text-emerald" },
  { name: "Application", icon: Cpu, color: "from-coral/15", accent: "text-coral" },
  { name: "Data", icon: Database, color: "from-cyan/15", accent: "text-cyan" },
  { name: "Security Operations", icon: Shield, color: "from-denim/15", accent: "text-denim" },
  { name: "Secure Data Sharing", icon: Lock, color: "from-amber-gold/15", accent: "text-amber-gold" },
  { name: "Encryption", icon: Server, color: "from-purple/15", accent: "text-purple" },
];

const CERTS = ["GDPR", "CCPA", "HIPAA", "SOX", "PCI-DSS", "FedRAMP", "ISO 27001", "SOC 2 Type II", "FIPS 140-3 L3", "CC EAL5+"];

const TRACK_RECORD = [
  { value: "0", label: "Security Breaches", sub: "15+ years" },
  { value: "99.9999%", label: "Uptime SLA", sub: "31.5s/yr max" },
  { value: "15+", label: "Years of Records", sub: "Continuous" },
  { value: "100%", label: "Compartmentalized", sub: "All operations" },
];

export function ComplianceSection() {
  return (
    <Section id="compliance" ariaLabel="Compliance, Security, and Ethical Standards" background="default">
      <SectionHeader
        icon={Shield}
        overline="Compliance & Security"
        overlineClassName="text-emerald"
        pillClassName="glass-tinted-emerald border-emerald/20"
        title={
          <>
            Sovereign-Grade <span className="text-gradient-success">Posture.</span>
          </>
        }
        subtitle="The work operates at the intersection of power, politics, and perception — and takes that responsibility with corresponding seriousness."
      />

      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-5 lg:gap-6 mb-8">
        <div className="glass-card rounded-2xl p-6">
          <div className="text-overline text-emerald mb-3">Zero-Trust Defense Layers</div>
          <h3 className="text-heading-md mb-4">Six security domains, fully covered.</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {COMPLIANCE.map((c, i) => (
              <motion.div
                key={c.name}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-gradient-to-br ${c.color} to-transparent border border-border/30`}
              >
                <c.icon className={`size-4 ${c.accent}`} />
                <span className="text-caption font-semibold">{c.name}</span>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="glass-card rounded-2xl p-6">
          <div className="text-overline text-amber-gold mb-3">Certifications</div>
          <h3 className="text-heading-md mb-4">Certified. Verified. Audited.</h3>
          <div className="flex flex-wrap gap-1.5">
            {CERTS.map((c) => (
              <span key={c} className="text-caption px-2.5 py-1 rounded-md bg-amber-gold/10 text-amber-gold border border-amber-gold/20 font-semibold">
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {TRACK_RECORD.map((t, i) => (
          <motion.div
            key={t.label}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
            className="glass-card rounded-xl p-4 bg-gradient-to-br from-emerald/15 to-transparent"
          >
            <CheckCircle2 className="size-5 text-emerald mb-2.5" />
            <div className="text-display-sm font-bold text-emerald leading-none mb-1.5">{t.value}</div>
            <div className="text-caption font-semibold uppercase tracking-wide mb-0.5">{t.label}</div>
            <div className="text-caption text-muted-foreground">{t.sub}</div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}
